import { Component, ViewChildren, QueryList, AfterViewInit } from '@angular/core';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { MatIconModule } from '@angular/material/icon';
import { ParticipantService } from '../../services/participant.service';
import { Participant } from '../../interfaces/participant';
import { Donor } from '../../interfaces/donor';
import { Recipient } from '../../interfaces/recipient';
import { CommonModule, KeyValue } from '@angular/common';
import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { ParticipantBoxComponent } from './participant-box/participant-box.component';
import { MatDialog } from '@angular/material/dialog';
import { ParticipantAddComponent } from '../participant/participant-add/participant-add.component';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { EnisImportComponent } from '../../enis-import/enis-import.component';
import { WarningChipsComponent } from '../../shell/warning-chips/warning-chips.component';
import { ParticipantEditComponent } from './participant-edit/participant-edit.component';
import { ParticipantDeleteDialogComponent } from './participant-delete-dialog/participant-delete-dialog.component';
import { DismissMultipleDonorWarningDialogComponent } from './dismiss-multiple-donor-warning-dialog/dismiss-multiple-donor-warning-dialog.component';
import { DismissAcceptableBloodtypesWarningDialogComponent } from './dismiss-acceptable-bloodtypes-warning-dialog/dismiss-acceptable-bloodtypes-warning-dialog.component';
import { DismissMaxDonorAgeWarningDialogComponent } from './dismiss-max-donor-age-warning-dialog/dismiss-max-donor-age-warning-dialog.component';
import { DismissMaxMismatchesWarningDialogComponent } from './dismiss-max-mismatches-warning-dialog/dismiss-max-mismatches-warning-dialog.component';
import { MinimatchSettingsDialogComponent } from './minimatch-settings-dialog/minimatch-settings-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Permission } from '../../interfaces/permission';
import { AuthService } from '../../auth/auth.service';
import { BehaviorSubject, debounceTime, distinctUntilChanged, map, Observable, combineLatest, startWith } from 'rxjs';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { RequestPriorityComponent } from '../approvals/request-priority/request-priority.component';
import { PairService } from '../../services/pair.service';
import { Pair, ReasonInactive } from '../../interfaces/pair';
import { PairAddComponent } from '../pair/pair-add/pair-add.component';
import { RequestPrivilegeComponent } from '../approvals/request-privilege/request-privilege.component';
import { RequestPrivilegeDonorComponent } from '../approvals/request-privilege-donor/request-privilege-donorcomponent';
import { MatchingPeriodService } from '../../services/matchingPeriod.service';
import { ActivatedRoute, Router } from '@angular/router';
import { RemoveDialogComponent } from '../pair/remove-dialog/remove-dialog.component';
import { UnpairDialogComponent } from '../pair/unpair-dialog/unpair-dialog.component';
import { ConfirmDialogComponent } from '../../shell/warning-chips/confirm-dialog/confirm-dialog.component';

@Component({
  selector: 'app-participants',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatMenuModule,
    PagetoolbarComponent,
    WarningChipsComponent,
    MatIconModule,
    CommonModule,
    MatExpansionModule,
    ParticipantBoxComponent,
    MatNativeDateModule,
    MatDatepickerModule,
    MatInputModule,
    MatTooltipModule,
    HighlightPipe
  ],
  templateUrl: './participants.component.html',
  styleUrl: './participants.component.scss'
})
export class ParticipantsComponent implements AfterViewInit {
  @ViewChildren(MatExpansionPanel) expansionPanels!: QueryList<MatExpansionPanel>;

  // Permissions
  canAddParticipant: boolean = false;
  canImportParticipants: boolean = false;
  canEditParticipant: boolean = false;
  canSetTransplanted: boolean = false;
  canAddPrivilegesParticipant: boolean = false;
  canAddPriorityParticipant: boolean = false;
  canRemoveParticipant: boolean = false;
  canAddPair: boolean = false;
  canEditPair: boolean = false;
  canInactivatePair: boolean = false;
  canDismissMultipleDonorWarning: boolean = false;
  canDismissAcceptableBloodTypesWarning: boolean = false;
  canDismissMaxDonorAgeWarning: boolean = false;
  canDismissMaxMismatchesWarning: boolean = false;
  canToggleMinimatchStatus: boolean = false;
  hasMinimatchInactive: boolean = false;
  isCenterUser: boolean = false;
  hasMatchrunStarted: boolean = false;
  isTogglingMinimatch: boolean = false;
  hasActiveParticipants: boolean = false;

  _filterButtons = new FormControl('nonpm');
  searchControl = new FormControl('');
  panelOpenState: boolean[] = [];
  pairs: Pair[] = [];
  currentDonorIndex: { [participantId: string]: number } = {}; // Track current donor index for each recipient

  private expandIdFromRoute?: string;
  private donorIdFromRoute?: string;
  private expandIdRetryCount: number = 0; // Prevent infinite loops when trying to expand

  readonly warningFilterMode$ = new BehaviorSubject<boolean>(false);
  readonly priorityFilterMode$ = new BehaviorSubject<boolean>(false);
  readonly pairFilterMode$ = new BehaviorSubject<boolean>(false);
  readonly activeFilterMode$ = new BehaviorSubject<boolean>(false);
  // PM filter has 3 states: 'nonpm' (show non-PM), 'all' (show all), 'onlypm' (show only PM)
  readonly pmFilterMode$ = new BehaviorSubject<'nonpm' | 'all' | 'onlypm'>('nonpm');
  
  // Dynamic labels that show default positive state when button is first selected
  readonly warningLabel$ = combineLatest([
    this.warningFilterMode$,
    this._filterButtons.valueChanges.pipe(startWith('nonpm'))
  ]).pipe(
    map(([filterMode, buttonValue]) => {
      // If warning filter button is selected and this is the first time, show "Warning"
      if (buttonValue === 'warning') {
        return filterMode ? 'Warning' : 'No Warnings';
      }
      return 'Warning'; // Default when button not selected
    })
  );

  readonly priorityLabel$ = combineLatest([
    this.priorityFilterMode$,
    this._filterButtons.valueChanges.pipe(startWith('nonpm'))
  ]).pipe(
    map(([filterMode, buttonValue]) => {
      if (buttonValue === 'priority') {
        return filterMode ? 'Priority/Privileges' : 'No Priority/Privileges';
      }
      return 'Priority/Privileges';
    })
  );

  readonly pairLabel$ = combineLatest([
    this.pairFilterMode$,
    this._filterButtons.valueChanges.pipe(startWith('nonpm'))
  ]).pipe(
    map(([filterMode, buttonValue]) => {
      if (buttonValue === 'pair') {
        return filterMode ? 'With Pairs' : 'Without Pairs';
      }
      return 'With Pairs';
    })
  );
  
  
  readonly activeLabel$ = combineLatest([
    this.activeFilterMode$,
    this._filterButtons.valueChanges.pipe(startWith('nonpm'))
  ]).pipe(
    map(([filterMode, buttonValue]) => {
      if (buttonValue === 'active') {
        return filterMode ? 'Active' : 'Inactive';
      }
      return 'Active';
    })
  );

  readonly pmLabel$ = combineLatest([
    this.pmFilterMode$,
    this._filterButtons.valueChanges.pipe(startWith('nonpm'))
  ]).pipe(
    map(([filterMode, buttonValue]) => {
      if (buttonValue === 'nonpm') {
        switch (filterMode) {
          case 'nonpm':
            return 'All Crossover';
          case 'all':
            return 'Show All';
          case 'onlypm':
            return 'Only PM waitlist';
          default:
            return 'All Crossover';
        }
      }
      return 'All Crossover';
    })
  );

  private readonly openKey$ = new BehaviorSubject<string | undefined>(undefined);

  constructor(
    private participantService: ParticipantService,
    private readonly dialog: MatDialog,
    private snackBar: MatSnackBar,
    private readonly authService: AuthService,
    private pairService: PairService,
    private matchingPeriodService: MatchingPeriodService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.openKey$.pipe(debounceTime(250)).subscribe(scrollTo);
  }

  participants: Participant[] = [];
  filteredParticipants: Participant[] = [];

  // Get total count of non-historical participants
  get totalParticipantsCount(): number {
    return this.participants.filter(participant => {
      if (participant.type === 'Donor') {
        const donor = participant as Donor;
        return !donor.historical;
      } else if (participant.type === 'Recipient') {
        const recipient = participant as Recipient;
        return !recipient.historical;
      }
      return true;
    }).length;
  }

  // Get actual count of participants in filtered list (counting donors + recipients separately)
  get filteredParticipantsCount(): number {
    let count = 0;
    for (const participant of this.filteredParticipants) {
      if (this.isRecipient(participant)) {
        const recipient = participant as Recipient;
        // Count the recipient
        count++;
        // Count linked donors
        if (recipient.linkedDonors && recipient.linkedDonors.length > 0) {
          count += recipient.linkedDonors.length;
        }
      } else {
        // Solo donor (altruist or standalone donor)
        count++;
      }
    }
    return count;
  }

  ngOnInit(): void {
    // Combine all three observables to ensure proper timing
    combineLatest([
      this.authService.getAllPermissions$(),
      this.authService.getUser(),
      this.matchingPeriodService.getLatestMatchingPeriod()
    ]).subscribe(([permissions, user, period]) => {
      // Set base permissions
      this.canAddParticipant = permissions.includes(Permission.PARTICIPANTS_ADD_INCLUDING_ALTRUISTS);
      this.canImportParticipants = permissions.includes(Permission.PARTICIPANTS_IMPORT_ENIS);
      this.canEditParticipant = permissions.includes(Permission.PARTICIPANTS_EDIT);
      this.canSetTransplanted = permissions.includes(Permission.PARTICIPANTS_SET_TRANSPLANTED);
      this.canRemoveParticipant = permissions.includes(Permission.PARTICIPANTS_EDIT);
      this.canAddPair = permissions.includes(Permission.PAIRS_LINK_PARTICIPANTS);
      this.canEditPair = permissions.includes(Permission.PAIRS_EDIT);
      this.canInactivatePair = permissions.includes(Permission.PAIRS_INACTIVATE);
      this.canAddPrivilegesParticipant = permissions.includes(Permission.PRIVILEGES_ADD);
      this.canAddPriorityParticipant = permissions.includes(Permission.PRIORITY_REQUEST);

      // Set role-based permissions
      const role = user.activeRole.enumName;
      this.canDismissMultipleDonorWarning = role === 'OPERATOR' || role === 'ALLOCATION';
      this.canDismissAcceptableBloodTypesWarning = role === 'OPERATOR' || role === 'ALLOCATION';
      this.canDismissMaxDonorAgeWarning = role === 'OPERATOR' || role === 'ALLOCATION';
      this.canDismissMaxMismatchesWarning = role === 'OPERATOR' || role === 'ALLOCATION';
      this.canToggleMinimatchStatus = role === 'OPERATOR' || role === 'ALLOCATION';
      this.isCenterUser = role === 'CENTER';

      // Check if at least one matchrun has started
      this.hasMatchrunStarted = !!(period?.matchRuns && period.matchRuns.length > 0);

      // Apply CENTER user restrictions after first matchrun starts
      if (this.isCenterUser && this.hasMatchrunStarted) {
        this.canEditPair = false;
        this.canInactivatePair = false;
      }
    });

    // Check for expandId query parameter
    this.route.queryParams.subscribe(params => {
      if (params['expandId']) {
        this.expandIdFromRoute = params['expandId'];
        this.donorIdFromRoute = params['donorId'] || undefined;
        this.expandIdRetryCount = 0; // Reset retry counter for new expandId

        // If participants are already loaded, try to expand immediately
        if (this.filteredParticipants.length > 0) {
          setTimeout(() => this.expandPanelFromRoute(), 300);
        }
      }
    });

    // Listen for new matching period creation to refresh minimatch status
    this.matchingPeriodService.onMatchingPeriodCreated.subscribe(() => {
      console.log('New matching period created, reloading participants and checking matchrun status');
      // Reload participants to get updated statuses
      this.loadParticipants();

      // Check if matchrun has started in the new matching period and re-apply CENTER user restrictions
      combineLatest([
        this.authService.getAllPermissions$(),
        this.authService.getUser(),
        this.matchingPeriodService.getLatestMatchingPeriod()
      ]).subscribe(([permissions, user, period]) => {
        // Re-set permissions (in case they changed)
        this.canEditPair = permissions.includes(Permission.PAIRS_EDIT);
        this.canInactivatePair = permissions.includes(Permission.PAIRS_INACTIVATE);

        // Update matchrun status
        this.hasMatchrunStarted = !!(period?.matchRuns && period.matchRuns.length > 0);

        // Re-apply CENTER user restrictions if matchrun has started
        const role = user.activeRole.enumName;
        this.isCenterUser = role === 'CENTER';
        if (this.isCenterUser && this.hasMatchrunStarted) {
          this.canEditPair = false;
          this.canInactivatePair = false;
        }
      });
    });

    this.loadParticipants();
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged()).subscribe((value) => {
      this.applySearch(value);
    });

    // Subscribe to matching period creation events to refresh data
    this.matchingPeriodService.onMatchingPeriodCreated.subscribe(() => {
      console.log('New matching period created, refreshing participants...');
      this.loadParticipants();
    });
  }

  ngAfterViewInit(): void {
    // Wait for expansion panels to be rendered
    this.expansionPanels.changes.subscribe(() => {
      setTimeout(() => this.expandPanelFromRoute(), 100);
    });
    // Also try immediately in case panels are already rendered
    setTimeout(() => this.expandPanelFromRoute(), 500);
  }

  private expandPanelFromRoute(): void {
    if (!this.expandIdFromRoute) return;

    // Find the participant index in filteredParticipants
    const participantIndex = this.filteredParticipants.findIndex(
      p => p.id === this.expandIdFromRoute
    );

    if (participantIndex === -1) {
      // Prevent infinite loops - only retry once
      if (this.expandIdRetryCount > 0) {
        this.expandIdFromRoute = undefined;
        this.donorIdFromRoute = undefined;
        this.expandIdRetryCount = 0;
        return;
      }

      // Check if participant exists in full list but is filtered out
      const participantInFullList = this.participants.find(p => p.id === this.expandIdFromRoute);
      if (participantInFullList) {
        this.expandIdRetryCount++;

        // If participant is a PM recipient, show all participants
        if (participantInFullList.type === 'Recipient' && (participantInFullList as any).postmortumwaitlist) {
          this.pmFilterMode$.next('all');
          this.applyFiltersAndSearch();
          return; // applyFiltersAndSearch will trigger expandPanelFromRoute again
        }

        // Otherwise, try showing all participants to find them
        this.pmFilterMode$.next('all');
        this.applyFiltersAndSearch();
        return;
      }

      // Participant not found at all - don't clear expandIdFromRoute yet
      return;
    }

    // Set the correct donor index if donorId is provided
    if (this.donorIdFromRoute && this.expandIdFromRoute) {
      const recipient = this.participants.find(p => p.id === this.expandIdFromRoute) as Recipient;
      if (recipient && recipient.linkedDonors) {
        const donorIndex = recipient.linkedDonors.findIndex(d => d.id === this.donorIdFromRoute);
        if (donorIndex !== -1) {
          this.currentDonorIndex[this.expandIdFromRoute] = donorIndex;
        }
      }
    }

    // Get the expansion panel at that index and open it
    const panelsArray = this.expansionPanels.toArray();
    if (panelsArray[participantIndex]) {
      panelsArray[participantIndex].open();
      // Scroll to the participant
      this.openKey$.next(this.expandIdFromRoute);
      // Clear the expandId and donorId so they don't reopen on future changes
      this.expandIdFromRoute = undefined;
      this.donorIdFromRoute = undefined;
      this.expandIdRetryCount = 0;
    }
  }

  // Load all participants
  loadParticipants(): void {
    this.participantService.getAllParticipants().subscribe({
      next: (data) => {
        this.participants = data;
        // Load pairs first, then apply filters
        this.loadPairs();
      },
      error: (err) => {
        console.error('Error fetching participants:', err);
      }
    });
  }

  // Apply filters and search on the frontend
  private applyFiltersAndSearch(): void {
    let filtered = [...this.participants];

    // Filter out historical participants (fully transplanted pairs)
    filtered = filtered.filter((participant) => {
      if (participant.type === 'Donor') {
        const donor = participant as Donor;
        return !donor.historical;
      } else if (participant.type === 'Recipient') {
        const recipient = participant as Recipient;
        return !recipient.historical;
      }
      return true;
    });

    // Apply PM filter - check if nonpm filter button is selected
    if (this._filterButtons.value === 'nonpm') {
      const pmMode = this.pmFilterMode$.value;

      if (pmMode === 'nonpm') {
        // Show non-PM participants (default)
        filtered = filtered.filter((participant) => {
          if (participant.type === 'Recipient') {
            const recipient = participant as Recipient;
            return !recipient.postmortumwaitlist;
          }
          return true; // Always show donors
        });
      } else if (pmMode === 'onlypm') {
        // Show only PM participants
        filtered = filtered.filter((participant) => {
          if (participant.type === 'Recipient') {
            const recipient = participant as Recipient;
            return recipient.postmortumwaitlist === true;
          }
          return false; // Don't show donors in "only PM" mode
        });
      }
      // If pmMode === 'all', show all participants (no filtering)
    } else {
      // For ALL other filter types (warning, priority, pair, active), exclude PM patients by default
      filtered = filtered.filter((participant) => {
        if (participant.type === 'Recipient') {
          const recipient = participant as Recipient;
          return !recipient.postmortumwaitlist;
        }
        return true; // Always show donors
      });
    }

    // Apply search filter
    const searchTerm = this.searchControl.value?.toLowerCase() || '';
    if (searchTerm) {
      filtered = filtered.filter((participant) => {
        const fullName = `${participant.firstName} ${participant.lastName}`.toLowerCase();
        const enisNumber = participant.enisNumber?.toLowerCase() || '';
        const center = participant.center?.name?.toLowerCase() || '';

        // Check if the participant itself matches
        const participantMatches = fullName.includes(searchTerm) || enisNumber.includes(searchTerm) || center.includes(searchTerm);

        // For recipients with pairs, also check if any linked donor matches
        if (this.isRecipient(participant)) {
          const recipient = participant as Recipient;
          if (recipient.linkedDonors && recipient.linkedDonors.length > 0) {
            const donorMatches = recipient.linkedDonors.some(donor => {
              const donorFullName = `${donor.firstName} ${donor.lastName}`.toLowerCase();
              const donorEnisNumber = donor.enisNumber?.toLowerCase() || '';
              const donorCenter = donor.center?.name?.toLowerCase() || '';
              return donorFullName.includes(searchTerm) || donorEnisNumber.includes(searchTerm) || donorCenter.includes(searchTerm);
            });
            return participantMatches || donorMatches;
          }
        }

        return participantMatches;
      });
    }

    // Apply warning filter - check if warning filter button is selected
    if (this._filterButtons.value === 'warning') {
      if (this.warningFilterMode$.value) {
        // Show ACTIVE participants WITH warnings only
        filtered = filtered.filter((participant) => this.hasWarnings(participant) && !this.shouldGrayOutParticipant(participant));
      } else {
        // Show participants WITHOUT warnings
        filtered = filtered.filter((participant) => !this.hasWarnings(participant));
      }
    }

    // Apply priority filter - check if priority filter button is selected
    if (this._filterButtons.value === 'priority') {
      if (this.priorityFilterMode$.value) {
        // Show participants WITH priority/privileges
        filtered = filtered.filter((participant) => this.hasPriority(participant));
      } else {
        // Show participants WITHOUT priority/privileges
        filtered = filtered.filter((participant) => !this.hasPriority(participant));
      }
    }

    // Apply pair filter - check if pair filter button is selected
    if (this._filterButtons.value === 'pair') {
      if (this.pairFilterMode$.value) {
        // Show participants WITH pairs
        filtered = filtered.filter((participant) => this.hasPair(participant));
      } else {
        // Show participants WITHOUT pairs
        filtered = filtered.filter((participant) => !this.hasPair(participant));
      }
    }


    // Apply active filter - check if active filter button is selected
    if (this._filterButtons.value === 'active') {
      if (this.activeFilterMode$.value) {
        // Show participants with ACTIVE pairs
        filtered = filtered.filter((participant) => this.hasActivePair(participant));
      } else {
        // Show participants with INACTIVE pairs
        filtered = filtered.filter((participant) => this.hasInactivePair(participant));
      }
    }

    // Filter out donors that are part of a pair - only show recipients for pairs
    // Keep solo donors (altruists or donors without a pair)
    // Also keep paired donors whose recipient was removed by an earlier filter (e.g. PM filter),
    // so they remain visible as standalone entries instead of disappearing entirely.
    const filteredRecipientIds = new Set(
      filtered.filter(p => p.type === 'Recipient').map(p => p.id)
    );
    filtered = filtered.filter((participant) => {
      if (this.isDonor(participant)) {
        const donor = participant as Donor;
        if (!donor.linkedRecipient) {
          return true; // Keep solo donors (altruists or donors without a pair)
        }
        // Keep paired donor only if their recipient was filtered out (so donor appears standalone)
        return !filteredRecipientIds.has(donor.linkedRecipient.id);
      }
      // Always show recipients
      return true;
    });

    // Sort participants: solo donors first, then solo recipients, then paired recipients
    filtered.sort((a, b) => {
      const aIsDonor = this.isDonor(a);
      const bIsDonor = this.isDonor(b);
      const aIsRecipient = this.isRecipient(a);
      const bIsRecipient = this.isRecipient(b);

      // Check if participants have pairs
      const aHasPair = aIsRecipient && (a as Recipient).linkedDonors && (a as Recipient).linkedDonors!.length > 0;
      const bHasPair = bIsRecipient && (b as Recipient).linkedDonors && (b as Recipient).linkedDonors!.length > 0;

      // Determine sort order (0 = solo donor, 1 = solo recipient, 2 = paired recipient)
      const aOrder = aIsDonor ? 0 : (aHasPair ? 2 : 1);
      const bOrder = bIsDonor ? 0 : (bHasPair ? 2 : 1);

      return aOrder - bOrder;
    });

    this.filteredParticipants = filtered;

    // Update minimatch status indicator
    this.updateMinimatchStatus();

    // If we have an expandId from route, try to expand after filtering
    if (this.expandIdFromRoute) {
      setTimeout(() => this.expandPanelFromRoute(), 300);
    }
  }

  // Check if participant has warnings
  private hasWarnings(participant: Participant): boolean {
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      const recipientHasWarning = (
        // Awaiting ENIS sync
        (!recipient.firstName && !recipient.lastName) ||
        // Missing Date of Birth
        recipient.dob === '1900-01-01' ||
        // Missing center
        recipient.center.name === 'UNKNOWN' ||
        // Missing both phenotypes
        ((!recipient.abPhenotype || recipient.abPhenotype === '') && (!recipient.drPhenotype || recipient.drPhenotype === '')) ||
        // Missing HLA Class I only
        ((!recipient.abPhenotype || recipient.abPhenotype === '') && recipient.drPhenotype && recipient.drPhenotype !== '') ||
        // Missing HLA Class II only
        ((!recipient.drPhenotype || recipient.drPhenotype === '') && recipient.abPhenotype && recipient.abPhenotype !== '') ||
        // Missing PRA
        (recipient.previousPRA == undefined && (recipient.virtualPRA == null && recipient?.unacceptables && recipient?.unacceptables?.length > 0)) ||
        // Outdated Screening (older than 151 days)
        this.hasOutdatedScreening(recipient) ||
        // Check: Multiple donors warning
        this.shouldShowMultipleDonorWarning(recipient) ||
        // Check: Acceptable blood types warning
        this.shouldShowAcceptableBloodTypesWarning(recipient) ||
        // Check: Max donor age warning
        this.shouldShowMaxDonorAgeWarning(recipient) ||
        // Check: Max mismatches warning
        this.shouldShowMaxMismatchesWarning(recipient)
      );
      if (recipientHasWarning) {
        return true;
      }
      // Check if any linked donors have warnings, so the pair still shows as
      // having warnings (and stays grouped) when only the donor side is affected
      if (recipient.linkedDonors && recipient.linkedDonors.length > 0) {
        return recipient.linkedDonors.some((donor) => this.hasDonorWarnings(donor));
      }
      return false;
    } else {
      const donor = participant as Donor;
      return this.hasDonorWarnings(donor);
    }
  }

  // Check if a donor has warnings (used both for standalone donors and linked donors of a recipient)
  private hasDonorWarnings(donor: Donor): boolean {
    return (
      // Awaiting ENIS sync
      (!donor.firstName && !donor.lastName) ||
      // Missing Date of Birth
      donor.dob === '1900-01-01' ||
      // Missing center
      donor.center.name === 'UNKNOWN' ||
      // Missing HLA
      donor.hla == undefined || donor.hla === ''
    );
  }

  // Check if recipient has outdated screening (older than 151 days)
  private hasOutdatedScreening(recipient: Recipient): boolean {
    if (!recipient.dateScreening) {
      return false;
    }

    if (recipient.selectedForTransplantation || recipient.transplanted) {
      return false;
    }

    const screeningDate = new Date(recipient.dateScreening);
    const today = new Date();
    const daysDifference = Math.floor((today.getTime() - screeningDate.getTime()) / (1000 * 60 * 60 * 24));

    return daysDifference > 151;
  }

  // Check if multiple donor warning should be shown
  private shouldShowMultipleDonorWarning(recipient: Recipient): boolean {
    if (!recipient || recipient.multipleDonorWarningDismissed) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = recipient.firstName && recipient.lastName;
    if (!enisSyncDone) {
      return false;
    }

    // Check if there are multiple donors
    if (!recipient.id) {
      return false;
    }
    const recipientPairs = this.pairs.filter((pair) => pair.recipient.id === recipient.id);
    const hasMultipleDonors = recipientPairs.length > 1;
    if (!hasMultipleDonors) {
      return false;
    }

    // Check if dateFirstDialysis is missing OR less than 2 years ago
    if (!recipient.dateFirstDialysis) {
      return true; // Missing dateFirstDialysis
    }

    const dialysisDate = new Date(recipient.dateFirstDialysis);
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    // If dialysis date is MORE RECENT (later) than 2 years ago, show warning
    return dialysisDate >= twoYearsAgo;
  }

  // Check if acceptable blood types warning should be shown
  private shouldShowAcceptableBloodTypesWarning(recipient: Recipient): boolean {
    if (!recipient || recipient.acceptableBloodTypesWarningDismissed) {
      return false;
    }

    // Only show warning if acceptable blood types privilege exists
    if (!recipient.acceptableBloodTypes || recipient.acceptableBloodTypes.trim() === '') {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = recipient.firstName && recipient.lastName;
    if (!enisSyncDone) {
      return false;
    }

    // Check if dateFirstDialysis is missing OR less than 2 years ago
    if (!recipient.dateFirstDialysis) {
      return true; // Missing dateFirstDialysis
    }

    const dialysisDate = new Date(recipient.dateFirstDialysis);
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

    // If dialysis date is MORE RECENT (later) than 2 years ago, show warning
    return dialysisDate >= twoYearsAgo;
  }

  // Check if max donor age warning should be shown (mirrors WarningChipsComponent.shouldShowMaxDonorAgeWarning)
  private shouldShowMaxDonorAgeWarning(recipient: Recipient): boolean {
    if (!recipient || recipient.maxDonorAgeWarningDismissed) {
      return false;
    }

    // Only show warning if max donor age privilege exists
    if (!recipient.maxDonorAge) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = recipient.firstName && recipient.lastName;
    if (!enisSyncDone) {
      return false;
    }

    // Don't show warning if recipient is part of a compatible pair
    if (recipient.id && this.pairs.some((pair) => pair.recipient.id === recipient.id && pair.compatible)) {
      return false;
    }

    // Don't show warning if recipient is younger than 18
    if (recipient.dob && this.isUnder18(recipient.dob)) {
      return false;
    }

    return true;
  }

  // Check if max mismatches warning should be shown (mirrors WarningChipsComponent.shouldShowMaxMismatchesWarning)
  private shouldShowMaxMismatchesWarning(recipient: Recipient): boolean {
    if (!recipient || recipient.maxMismatchesWarningDismissed) {
      return false;
    }

    // Only show warning if any of the max mismatches privileges exist
    const hasMaxMismatchesPrivilege = !!(recipient.maxMismatches || recipient.maxMmA || recipient.maxMmB || recipient.maxMmDR);
    if (!hasMaxMismatchesPrivilege) {
      return false;
    }

    // Only show warning after ENIS sync (firstName and lastName must exist)
    const enisSyncDone = recipient.firstName && recipient.lastName;
    if (!enisSyncDone) {
      return false;
    }

    // Don't show warning if recipient is part of a compatible pair
    if (recipient.id && this.pairs.some((pair) => pair.recipient.id === recipient.id && pair.compatible)) {
      return false;
    }

    // Don't show warning if recipient is younger than 18
    if (recipient.dob && this.isUnder18(recipient.dob)) {
      return false;
    }

    return true;
  }

  // Check if participant has priority/privileges
  private hasPriority(participant: Participant): boolean {
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;

      // Check recipient priority flag
      if (recipient.priority) {
        return true;
      }

      // Check if recipient has multiple donors
      if (recipient.id) {
        const recipientPairs = this.pairs.filter((pair) => pair.recipient.id === recipient.id);
        if (recipientPairs.length > 1) {
          return true;
        }
      }

      // Check privileges (same logic as warning-chips getPrivileges method)
      if (
        this.isUnder18(recipient.dob) ||
        recipient?.acceptableBloodTypes ||
        recipient?.acceptableHla ||
        recipient?.maxDonorAge ||
        recipient?.maxMismatches ||
        recipient?.extraWaitTime ||
        recipient?.lowMfi1 ||
        recipient?.lowMfi2
      ) {
        return true;
      }

      // Check if any linked donors have privileges (limitedToCenter)
      if ((recipient as any).linkedDonors && (recipient as any).linkedDonors.length > 0) {
        const hasLinkedDonorPrivileges = (recipient as any).linkedDonors.some((donor: Donor) => {
          return donor.limitedToCenter && donor.limitedToCenter.length > 0;
        });
        if (hasLinkedDonorPrivileges) {
          return true;
        }
      }
    } else {
      const donor = participant as Donor;
      // Check donor privileges (limited to center)
      if (donor.limitedToCenter && donor.limitedToCenter.length > 0) {
        return true;
      }
    }

    return false;
  }

  // Helper method to check if recipient is under 18 (same as warning-chips)
  private isUnder18(dob: string): boolean {
    if (!dob) return false;
    
    const birthDate = new Date(dob);
    const today = new Date();

    // Calculate the age
    const age = today.getFullYear() - birthDate.getFullYear();

    // Check if the birthday has occurred this year
    const hasBirthdayPassed = today.getMonth() > birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

    // If birthday hasn't passed yet, subtract one year
    return hasBirthdayPassed ? age < 18 : age - 1 < 18;
  }

  // Check if participant is part of a pair
  private hasPair(participant: Participant): boolean {
    if (!participant.id) {
      return false;
    }

    // Check if participant has any pairs (active or inactive)
    const participantPairs = this.pairs.filter((pair) => 
      pair.donor.id === participant.id || pair.recipient.id === participant.id
    );

    return participantPairs.length > 0;
  }


  // Check if participant has active pairs or is an active solo participant
  hasActivePair(participant: Participant): boolean {
    if (!participant.id) {
      return false;
    }

    const participantPairs = this.pairs.filter((pair) =>
      pair.donor.id === participant.id || pair.recipient.id === participant.id
    );


    // If participant has pairs, check if any are active
    if (participantPairs.length > 0) {
      return participantPairs.some(pair => pair.active);
    }

    // If no pairs, check if it's an active solo participant
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      // Check for altruistic donors or solo donors
      return donor.active === true;
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      // Check for solo recipients
      return recipient.active !== false; // Active if not explicitly set to false
    }

    return false;
  }

  // Check if participant has inactive pairs or is an inactive solo participant
  private hasInactivePair(participant: Participant): boolean {
    if (!participant.id) {
      return false;
    }

    const participantPairs = this.pairs.filter((pair) =>
      pair.donor.id === participant.id || pair.recipient.id === participant.id
    );

    // If participant has pairs, check if any are inactive
    if (participantPairs.length > 0) {
      return participantPairs.some(pair => !pair.active);
    }

    // If no pairs, check if it's an inactive solo participant
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      // Check for altruistic donors or solo donors
      return donor.active === false;
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      // Check for solo recipients
      return recipient.active === false;
    }

    return false;
  }

  isDonor(participant: Participant): participant is Donor {
    return participant.type === 'Donor';
  }

  isRecipient(participant: Participant): participant is Recipient {
    return participant.type === 'Recipient';
  }

  sendParticipantBox(participant: Participant): Recipient {
    let recipient: Recipient;
    if (this.isDonor(participant)) {
      // Transform Donor into Recipient by copying common fields and filling missing ones with null/defaults
      recipient = {
        ...participant // Copy the shared properties
      };
    } else {
      // If it's already a Recipient, just return it as is
      recipient = participant;
    }

    return recipient;
  }

  addParticipant(): void {
    const dialogRef = this.dialog.open(ParticipantAddComponent);

    // Subscribe to the response without closing the dialog
    dialogRef.componentInstance.responseSubjectNewDonor.subscribe((response: Donor) => {
      const newParticipant: Participant = {
        ...response,
        type: 'Donor'
      };
      this.participants.unshift(newParticipant);
      // Apply filters and search to update the displayed list
      this.applyFiltersAndSearch();
    });

    // Subscribe to the response without closing the dialog
    dialogRef.componentInstance.responseSubjectNewRecipient.subscribe((response: Recipient) => {
      const newParticipant: Participant = {
        ...response,
        type: 'Recipient'
      };
      this.participants.unshift(newParticipant);
      // Apply filters and search to update the displayed list
      this.applyFiltersAndSearch();
    });

    // Subscribe to new pair creation
    dialogRef.componentInstance.responseSubjectNewPair.subscribe((response: Pair) => {
      // Add the pair to the pairs list
      this.pairs.unshift(response);
      // Re-map linked participants to reflect the new pair
      this.mapLinkedParticipants();
      // Apply filters and search to update the displayed list
      this.applyFiltersAndSearch();
    });
  }

  importEnisData(): void {
    const dialogRef = this.dialog.open(EnisImportComponent);

    // Listen for the processed event to know when import is complete
    dialogRef.componentInstance.processed.subscribe((count: number) => {
      console.log(`ENIS import completed with ${count} participants`);
      // Reload all participants to get the updated order and data
      this.loadParticipants();
    });

    dialogRef.componentInstance.responseSubjectNewRecipientList.subscribe((response: Recipient[]) => {
      console.log('Received response:', response);

      // Map each Recipient to a Participant and add the `type` property
      const newParticipants: Participant[] = response.map((recipient) => ({
        ...recipient,
        type: 'Recipient'
      }));

      // Check if participants already exist by enis number and replace or add
      newParticipants.forEach((newParticipant) => {
        const existingIndex = this.filteredParticipants.findIndex((existing) => existing.enisNumber === newParticipant.enisNumber);

        if (existingIndex !== -1) {
          // Replace existing participant
          this.filteredParticipants[existingIndex] = newParticipant;
        } else {
          // Add new participant to the beginning
          this.filteredParticipants.unshift(newParticipant);
        }
      });
    });

    dialogRef.componentInstance.responseSubjectNewDonorList.subscribe((response: Donor[]) => {
      console.log('Received response:', response);

      // Map each Donor to a Participant and add the `type` property
      const newParticipants: Participant[] = response.map((donor) => ({
        ...donor,
        type: 'Donor'
      }));

      // Check if participants already exist by enis number and replace or add
      newParticipants.forEach((newParticipant) => {
        const existingIndex = this.filteredParticipants.findIndex((existing) => existing.enisNumber === newParticipant.enisNumber);

        if (existingIndex !== -1) {
          // Replace existing participant
          this.filteredParticipants[existingIndex] = newParticipant;
        } else {
          // Add new participant to the beginning
          this.filteredParticipants.unshift(newParticipant);
        }
      });
    });
  }

  removeParticipant(participant: Participant): void {
    const dialogRef = this.dialog.open(ParticipantDeleteDialogComponent, {
      data: { participant: participant },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed && participant.id != undefined) {
        const reason = result.reason;
        
        if (participant.type === 'Donor') {
          this.participantService.deleteDonor(participant.id, reason).subscribe({
            next: (response) => {
              console.log('Donor archived:', response);
              this.showNotification(`Donor ${participant.enisNumber} archived: ${reason}`);
              this.participants = this.participants.filter((item) => item.id !== participant.id);
              this.filteredParticipants = this.filteredParticipants.filter((item) => item.id !== participant.id);
            },
            error: (err) => {
              console.error('Error:', err);
              this.showNotification(err.message);
            }
          });
        } else {
          this.participantService.deleteRecipient(participant.id, reason).subscribe({
            next: (response) => {
              console.log('Recipient archived:', response);
              this.showNotification(`Recipient ${participant.enisNumber} archived: ${reason}`);
              this.participants = this.participants.filter((item) => item.id !== participant.id);
              this.filteredParticipants = this.filteredParticipants.filter((item) => item.id !== participant.id);
            },
            error: (err) => {
              console.error('Error:', err);
              this.showNotification(err.message);
            }
          });
        }
      }
    });
  }

  editParticipant(participant: Participant): void {
    // Store currently expanded participant to restore after edit
    const currentlyExpandedId = this.openKey$.getValue();

    const dialogRef = this.dialog.open(ParticipantEditComponent, {
      data: { participant: participant }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Find the index of the pair with the same id
        const index = this.participants.findIndex((p) => p.id === result.id);

        // Replace the existing pair if found
        if (index !== -1) {
          this.participants[index] = result;
        } else {
          console.warn('Participant with the specified ID was not found.');
        }
        this.mapLinkedParticipants();
        console.log('Updated participant:', result);
        this.applyFiltersAndSearch();

        // Restore expanded panel to the edited participant
        if (currentlyExpandedId) {
          this.expandIdFromRoute = currentlyExpandedId;
          this.expandIdRetryCount = 0;
          // Trigger expansion after filters have been applied
          setTimeout(() => this.expandPanelFromRoute(), 300);
        }
      }
    });
  }

  private applySearch(searchTerm: string | null): void {
    this.applyFiltersAndSearch();
  }

  //This should be a general function accessible using an import, as it's now copied in a lot of seperate components.
  showNotification(message: string, action: string = 'Close'): void {
    this.snackBar.open(message, action, {
      duration: 3000,
      verticalPosition: 'top',
      horizontalPosition: 'center'
    });
  }

  isAllowedPrivileges(participant: Participant): boolean {
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      // check if recipient is younger then 18 years, return true if so

      if (recipient.dob) {
        const birthDate = new Date(recipient.dob);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (age < 18) {
          return true; // Return true if recipient is younger than 18 years
        }
      }

      if (recipient.linkedDonors && recipient.linkedDonors.length > 0 && recipient.linkedDonors[0].id) {
        let pair = this.getPairByDonorId(recipient.linkedDonors[0].id);
        if (pair && pair.compatible) {
          return true; // Return true if there is a compatible pair
        }
      }
    } else if (participant.type === 'Donor') {
      const donor = participant as Donor;
      if (donor.altruist) {
        return true; // Return true if donor is altruist
      }
    }
    return false; // Return false if checks arent there.
  }

  setFilterMode(filterType: string, event?: Event) {
    // Prevent default toggle behavior
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // If clicking the same filter that's already selected, toggle between modes
    if (this._filterButtons.value === filterType) {
      this.toggleCurrentFilter(filterType);
    } else {
      // If clicking a different filter, set it and start with default mode
      this._filterButtons.setValue(filterType);
      this.resetAllFilters();

      switch (filterType) {
        case 'nonpm':
          this.pmFilterMode$.next('nonpm'); // Start with "non-PM"
          break;
        case 'warning':
          this.warningFilterMode$.next(true); // Start with "with warnings"
          break;
        case 'priority':
          this.priorityFilterMode$.next(true); // Start with "with priority"
          break;
        case 'pair':
          this.pairFilterMode$.next(true); // Start with "with pairs"
          break;
        case 'active':
          this.activeFilterMode$.next(true); // Start with "active"
          break;
      }
    }

    this.applyFiltersAndSearch();
  }

  private toggleCurrentFilter(filterType: string) {
    switch (filterType) {
      case 'nonpm':
        // Cycle through: nonpm → onlypm → all → nonpm
        const currentPmMode = this.pmFilterMode$.value;
        if (currentPmMode === 'nonpm') {
          this.pmFilterMode$.next('onlypm');
        } else if (currentPmMode === 'onlypm') {
          this.pmFilterMode$.next('all');
        } else {
          this.pmFilterMode$.next('nonpm');
        }
        break;
      case 'warning':
        this.warningFilterMode$.next(!this.warningFilterMode$.value);
        break;
      case 'priority':
        this.priorityFilterMode$.next(!this.priorityFilterMode$.value);
        break;
      case 'pair':
        this.pairFilterMode$.next(!this.pairFilterMode$.value);
        break;
      case 'active':
        this.activeFilterMode$.next(!this.activeFilterMode$.value);
        break;
    }
  }

  private resetAllFilters() {
    this.pmFilterMode$.next('nonpm');
    this.warningFilterMode$.next(false);
    this.priorityFilterMode$.next(false);
    this.pairFilterMode$.next(false);
    this.activeFilterMode$.next(false);
  }

  requestPriority(recipient: Recipient) {
    const dialogRef = this.dialog.open(RequestPriorityComponent, {
      data: recipient
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // Store the current participant ID to re-open it after reload
        const currentParticipantId = this.openKey$.getValue();

        if (currentParticipantId) {
          // Update query params to include expandId
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { expandId: currentParticipantId },
            queryParamsHandling: 'merge'
          });
        }

        // Reload participants to refresh pending approval status
        this.loadParticipants();
      }
    });
  }

  requestPrivilege(recipient: Recipient) {
    const dialogRef = this.dialog.open(RequestPrivilegeComponent, {
      data: recipient
    });

    dialogRef.afterClosed().subscribe((result) => {
      result = result.result;
      if (result) {
        // Find the index of the pair with the same id
        const index = this.participants.findIndex((p) => p.id === result.id);

        // Replace the existing pair if found
        if (index !== -1) {
          this.participants[index] = result;
        } else {
          console.warn('Participant with the specified ID was not found.');
        }
        this.mapLinkedParticipants();
        console.log('Updated participant:', result);
      }
    });
  }

  requestPrivilegeDonor(donor: Donor) {
    const dialogRef = this.dialog.open(RequestPrivilegeDonorComponent, {
      data: donor
    });

    dialogRef.afterClosed().subscribe((result) => {
      result = result.result;
      if (result) {
        // Find the index of the pair with the same id
        const index = this.participants.findIndex((p) => p.id === result.id);

        // Replace the existing pair if found
        if (index !== -1) {
          this.participants[index] = result;
        } else {
          console.warn('Participant with the specified ID was not found.');
        }
        this.mapLinkedParticipants();
        console.log('Updated participant:', result);
      }
    });
  }

  select(participant: Participant) {
    this.openKey$.next(participant.id);
  }

  private loadPairs(): void {
    this.pairService.getAllPairs().subscribe({
      next: (data) => {
        this.pairs = data;
        this.mapLinkedParticipants(); // Map linked participants after loading pairs
        this.applyFiltersAndSearch(); // Apply filters after pairs are loaded

        // Restore expanded panel if expandIdFromRoute is set (after transplantation report refresh)
        if (this.expandIdFromRoute) {
          setTimeout(() => this.expandPanelFromRoute(), 300);
        }
      },
      error: (error) => {
        console.log(error);
        // Even if pairs fail to load, we should still show participants
        this.applyFiltersAndSearch();

        // Restore expanded panel even if pairs fail to load
        if (this.expandIdFromRoute) {
          setTimeout(() => this.expandPanelFromRoute(), 300);
        }
      }
    });
  }
  private mapLinkedParticipants(): void {
    this.participants.forEach((participant) => {
      if (this.isDonor(participant)) {
        // Find the pair where this donor is linked to a recipient
        const linkedPair = this.pairs.find((pair) => pair.donor.id === participant.id);
        if (linkedPair) {
          (participant as any).linkedRecipient = linkedPair.recipient; // Add linked recipient
        } else {
          (participant as any).linkedRecipient = undefined; // Clear if no pair exists
        }
      } else if (this.isRecipient(participant)) {
        // Find all pairs where this recipient is linked to donors
        const linkedPairs = this.pairs.filter((pair) => pair.recipient.id === participant.id);
        (participant as any).linkedDonors = linkedPairs.map((pair) => pair.donor); // Add linked donors (or empty array if no pairs)
        this.clampCurrentDonorIndex(participant.id, linkedPairs.length);
      }
    });
  }

  /**
   * The donor navigation index survives a pairs reload, so after unpairing or archiving the
   * donor that is currently being viewed it can point past the end of the list. getCurrentDonor()
   * then returns undefined, which hides the donor box and the pair action row, and with one donor
   * left the navigation arrows are gone too - leaving no way back without a full page refresh.
   */
  private clampCurrentDonorIndex(participantId: string | undefined, donorCount: number): void {
    if (!participantId) return;

    const index = this.currentDonorIndex[participantId];
    if (index === undefined) return;

    if (donorCount === 0) {
      delete this.currentDonorIndex[participantId];
    } else if (index > donorCount - 1) {
      this.currentDonorIndex[participantId] = donorCount - 1;
    }
  }
  getPairByDonorId(donorId: string): Pair | undefined {
    return this.pairs.find((pair) => pair.donor.id === donorId);
  }

  getPairByParticipantId(participantId: string): Pair | undefined {
    return this.pairs.find((pair) => pair.donor.id === participantId || pair.recipient.id === participantId);
  }

  // Get all pairs for a specific participant (useful for recipients with multiple donors)
  getAllPairsForParticipant(participantId: string): Pair[] {
    return this.pairs.filter((pair) => pair.donor.id === participantId || pair.recipient.id === participantId);
  }

  // Check if participant should be grayed out (has inactive pairs or is inactive)
  shouldGrayOutParticipant(participant: Participant): boolean {
    if (!participant.id) return false;

    // Check if it's an inactive donor (altruist or regular)
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      if (donor.active === false) {
        return true;
      }
    }

    // Check if it's a recipient (multi-donor group or single pair)
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      const participantPairs = this.getAllPairsForParticipant(participant.id);

      // For multi-donor groups: only gray out based on recipient status
      if (participantPairs.length > 1) {
        return recipient.active === false;
      }

      // For single recipients: gray out if recipient is inactive OR if the single pair is inactive
      if (recipient.active === false) {
        return true;
      }

      // Check if the single pair is inactive
      if (participantPairs.length === 1 && !participantPairs[0].active) {
        return true;
      }
    }

    return false;
  }

  // Check if participant is an inactive altruistic donor
  isInactiveAltruist(participant: Participant): boolean {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      return donor.altruist === true && donor.active === false;
    }
    return false;
  }

  // Get inactive altruistic donor with reason (similar to getSingleInactivePair)
  getInactiveAltruist(participant: Participant): Donor | null {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      if (donor.altruist === true && donor.active === false) {
        return donor;
      }
    }
    return null;
  }

  // Get inactive regular donor (non-altruist, no pairs)
  getInactiveDonor(participant: Participant): Donor | null {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      const participantPairs = this.getAllPairsForParticipant(participant.id || '');
      if (!donor.altruist && donor.active === false && participantPairs.length === 0) {
        return donor;
      }
    }
    return null;
  }

  // Get inactive recipient (no pairs)
  getInactiveRecipient(participant: Participant): Recipient | null {
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      const participantPairs = this.getAllPairsForParticipant(participant.id || '');
      if (recipient.active === false && participantPairs.length === 0) {
        return recipient;
      }
    }
    return null;
  }

  // Check if participant has a single inactive pair (for showing banner at panel level)
  getSingleInactivePair(participant: Participant): Pair | null {
    if (!participant.id) return null;

    const participantPairs = this.getAllPairsForParticipant(participant.id);

    // Only show banner for single pairs that are inactive (not multi-donor scenarios)
    if (participantPairs.length === 1 && !participantPairs[0].active) {
      return participantPairs[0];
    }

    return null;
  }

  // Get inactive recipient with multiple pairs (for showing banner at panel level)
  getInactiveRecipientWithMultiplePairs(participant: Participant): Recipient | null {
    if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      const participantPairs = this.getAllPairsForParticipant(participant.id || '');
      // Show banner ONLY if recipient is inactive and has MORE than 1 pair (multi-donor scenario)
      if (recipient.active === false && participantPairs.length > 1) {
        return recipient;
      }
    }
    return null;
  }

  onDonorUpdated(updatedDonor: Donor): void {
    console.log('Donor updated:', updatedDonor);

    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    // If donor is now transplanted or historical, reload participants to get updated matched partners
    if (updatedDonor.transplanted || updatedDonor.historical) {
      console.log('Donor transplanted or historical, reloading all participants to refresh matched partners...');

      // Store the expanded ID to restore after reload
      if (currentlyExpandedId) {
        this.expandIdFromRoute = currentlyExpandedId;
        this.expandIdRetryCount = 0;
      }

      // Load fresh data from API (will cascade: loadParticipants -> loadPairs -> mapLinkedParticipants -> applyFiltersAndSearch)
      this.loadParticipants();
      return;
    }

    // Update the donor in the participants list
    const donorIndex = this.participants.findIndex((p) => p.id === updatedDonor.id);
    if (donorIndex !== -1) {
      // Preserve linkedRecipient when updating
      const linkedRecipient = (this.participants[donorIndex] as Donor).linkedRecipient;
      this.participants[donorIndex] = { ...updatedDonor, linkedRecipient, type: 'Donor' };

      // Force Angular to detect the change by creating a new array reference
      this.participants = [...this.participants];

      // Reapply filters to update the UI immediately
      this.applyFiltersAndSearch();
    }
  }

  onRecipientUpdated(updatedRecipient: Recipient): void {
    console.log('Recipient updated:', updatedRecipient);

    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    // If recipient is now transplanted or historical, reload participants to get updated matched partners
    if (updatedRecipient.transplanted || updatedRecipient.historical) {
      console.log('Recipient transplanted or historical, reloading all participants to refresh matched partners...');

      // Store the expanded ID to restore after reload
      if (currentlyExpandedId) {
        this.expandIdFromRoute = currentlyExpandedId;
        this.expandIdRetryCount = 0;
      }

      // Load fresh data from API (will cascade: loadParticipants -> loadPairs -> mapLinkedParticipants -> applyFiltersAndSearch)
      this.loadParticipants();
      return;
    }

    // Update the recipient in the participants list
    const recipientIndex = this.participants.findIndex((p) => p.id === updatedRecipient.id);
    if (recipientIndex !== -1) {
      // Preserve linkedDonors when updating
      const linkedDonors = (this.participants[recipientIndex] as Recipient).linkedDonors;
      this.participants[recipientIndex] = { ...updatedRecipient, linkedDonors, type: 'Recipient' };

      // Force Angular to detect the change by creating a new array reference
      this.participants = [...this.participants];

      // Reapply filters to update the UI immediately
      this.applyFiltersAndSearch();
    }
  }

  /**
   * A participant state change cascades to its pair(s) and to the other participant on the
   * backend (PairStateService), so the affected pairs are refetched here instead of being
   * guessed client-side (CROS-398).
   */
  private refreshPairsForParticipant(participantId?: string): void {
    if (!participantId) return;

    this.getAllPairsForParticipant(participantId).forEach((pair) => {
      this.pairService.getPair(pair.id).subscribe({
        next: (updatedPair) => {
          if (updatedPair) {
            this.onPairUpdated(updatedPair);
          }
        },
        error: (err) => {
          console.error('Error refreshing pair after participant change:', err);
        }
      });
    });
  }

  onPairUpdated(updatedPair: Pair): void {
    console.log(updatedPair);

    // Update the pair in the pairs array
    const pairIndex = this.pairs.findIndex((pair) => pair.id === updatedPair.id);
    if (pairIndex !== -1) {
      this.pairs[pairIndex] = updatedPair;
    }

    // Update the pair in the participants list
    const donor = this.participants.find((p) => p.id === updatedPair.donor.id);
    if (donor) {
      (donor as any).linkedRecipient = updatedPair.recipient;
    }

    const recipient = this.participants.find((p) => p.id === updatedPair.recipient.id);
    if (recipient) {
      (recipient as any).linkedDonors = this.pairs.filter((pair) => pair.recipient.id === recipient.id).map((pair) => pair.donor);
    }
  }

  onPairDeleted(archivedPair: Pair): void {
    // Reload all pairs to refresh participant pairing information
    this.loadPairs();
    console.log('Pair deleted/unpaired, reloading pairs to refresh participant links:', archivedPair.id);
  }
  addPair(participant?: Participant) {
    const dialogRef = this.dialog.open(PairAddComponent, {
      data: participant // Pass the clicked participant (donor or recipient) to the dialog
    });

    dialogRef.componentInstance.responseSubjectNewPair.subscribe((response: Pair) => {
      console.log('Received response:', response);
      this.pairs.unshift(response);

      // Add the recipient from the new pair to the donor's linkedRecipient
      if (response.recipient) {
        const donor = this.participants.find((p) => p.id === response.donor.id) as Donor;
        if (donor) {
          donor.linkedRecipient = response.recipient; // Update the linkedRecipient property

          // Ensure the recipient also has the donor in its linkedDonors
          const recipient = this.participants.find((p) => p.id === response.recipient.id) as Recipient;
          if (recipient) {
            if (!recipient.linkedDonors) {
              recipient.linkedDonors = []; // Initialize linkedDonors if not already set
            }
            if (!recipient.linkedDonors.find((d) => d.id === donor.id)) {
              recipient.linkedDonors.push(donor); // Add the donor to linkedDonors if not already present
            }
          }
        }
      }
      // Add the donor from the new pair to the recipient's linkedDonors
      if (response.donor) {
        const recipient = this.participants.find((p) => p.id === response.recipient.id) as Recipient;
        if (recipient) {
          if (!recipient.linkedDonors) {
            recipient.linkedDonors = []; // Initialize linkedDonors if not already set
          }
          if (!recipient.linkedDonors.find((d) => d.id === response.donor.id)) {
            recipient.linkedDonors.push(response.donor); // Add the donor to linkedDonors if not already present
          }

          // Ensure the donor also has the recipient in its linkedRecipient
          const donor = this.participants.find((p) => p.id === response.donor.id) as Donor;
          if (donor) {
            donor.linkedRecipient = recipient; // Update the linkedRecipient property
          }
        }
      }

      // Determine which participant to re-open
      // After pairing, donors are filtered out and only recipients are shown for pairs
      // So we always want to re-open the recipient's panel and show the newly added donor
      const participantIdToReopen = response.recipient.id;

      if (participantIdToReopen) {
        this.expandIdFromRoute = participantIdToReopen;
        this.donorIdFromRoute = response.donor.id; // Set the donor to display
        this.expandIdRetryCount = 0;
        this.applyFiltersAndSearch();
      }
    });
  }

  canAddDonor(participant: Participant): boolean {
    if (participant.type === 'Recipient') {
      const linkedDonors = (participant as any).linkedDonors;
      // Allow up to 3 donors per recipient
      if (linkedDonors === undefined || linkedDonors === null || linkedDonors.length < 3) {
        return true;
      }
    }
    return false;
  }

  canAddRecipient(participant: Participant): boolean {
    if (participant.type === 'Donor') {
      const linkedRecipient = (participant as any).linkedRecipient;
      const donor = participant as Donor;
      if (linkedRecipient || donor.altruist) {
        return false; // Do not allow adding a recipient if there are linked recipients
      } else {
        return true; // Allow adding a recipient if there are no linked donors
      }
    }
    return false;
  }

  addDonor(participant: Participant) {
    this.addPair(participant);
  }
  addRecipient(participant: Participant) {
    this.addPair(participant);
  }

  // Check if participant is locked (either in a locked chain or selected for transplantation)
  isParticipantLocked(participant: Participant): boolean {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      return !!(donor.locked || donor.selectedForTransplantation);
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      return !!(recipient.locked || recipient.selectedForTransplantation);
    }
    return false;
  }

  // Helper method for banner display - checks if participant is locked
  isParticipantLockedHelper(participant: Participant): boolean {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      return !!(donor.locked || donor.selectedForTransplantation);
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      return !!(recipient.locked || recipient.selectedForTransplantation);
    }
    return false;
  }

  // Helper method for banner display - gets appropriate lock message
  getParticipantLockMessage(participant: Participant): string {
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      const isSelectedForTransplant = !!(donor.selectedForTransplantation);
      const isTransplanted = !!(donor.transplanted);
      if (isSelectedForTransplant && isTransplanted) {
        return 'Donor: Transplantation confirmed';
      } else if (isSelectedForTransplant) {
        return 'Donor: Selected for transplantation';
      } else {
        return 'Donor: Locked';
      }
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      const isSelectedForTransplant = !!(recipient.selectedForTransplantation);
      const isTransplanted = !!(recipient.transplanted);
      if (isSelectedForTransplant && isTransplanted) {
        return 'Recipient: Transplantation confirmed';
      } else if (isSelectedForTransplant) {
        return 'Recipient: Selected for transplantation';
      } else {
        return 'Recipient: Locked';
      }
    }
    return '';
  }

  // Check if CENTER user can edit this participant
  // CENTER users can only edit participants that are selected for transplantation (to confirm transplant)
  // This restriction only applies AFTER the first matchrun has started
  canCenterUserEditParticipant(participant: Participant): boolean {
    if (!this.isCenterUser) {
      return true; // Non-CENTER users follow normal permissions
    }

    // If no matchrun has started yet, allow the edit (no restrictions before first matchrun)
    if (!this.hasMatchrunStarted) {
      return true;
    }

    // CENTER users can only edit if participant is selected for transplantation
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      return !!donor.selectedForTransplantation;
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      return !!recipient.selectedForTransplantation;
    }
    return false;
  }

  // Check if CENTER user can archive this participant
  // CENTER users can only archive inactive participants
  // This restriction only applies AFTER the first matchrun has started
  canCenterUserArchiveParticipant(participant: Participant): boolean {
    if (!this.isCenterUser) {
      return true; // Non-CENTER users follow normal permissions
    }

    // If no matchrun has started yet, allow the archive (no restrictions before first matchrun)
    if (!this.hasMatchrunStarted) {
      return true;
    }

    // CENTER users can only archive if participant is inactive
    if (participant.type === 'Donor') {
      const donor = participant as Donor;
      return donor.active === false;
    } else if (participant.type === 'Recipient') {
      const recipient = participant as Recipient;
      return recipient.active === false;
    }
    return false;
  }

  onMultipleDonorWarningClick(recipient: Recipient): void {
    // Store currently expanded participant to restore after dismissing the warning
    const currentlyExpandedId = this.openKey$.getValue();

    const dialogRef = this.dialog.open(DismissMultipleDonorWarningDialogComponent, {
      data: { recipient }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        // Call API to dismiss the warning
        this.participantService.dismissMultipleDonorWarning(recipient.id!, result.reason).subscribe({
          next: (updatedRecipient) => {
            this.snackBar.open('Multiple donor warning dismissed successfully', 'Close', {
              duration: 3000
            });

            // Update the recipient in the participants list
            const recipientIndex = this.participants.findIndex((p) => p.id === recipient.id);
            if (recipientIndex !== -1) {
              // Preserve linkedDonors when updating
              const linkedDonors = (this.participants[recipientIndex] as Recipient).linkedDonors;
              this.participants[recipientIndex] = { ...updatedRecipient, linkedDonors, type: 'Recipient' };

              // Force Angular to detect the change by creating a new array reference
              this.participants = [...this.participants];

              // Reapply filters to update the UI immediately
              this.applyFiltersAndSearch();

              // Restore expanded panel to the pair the user was working on
              if (currentlyExpandedId) {
                this.expandIdFromRoute = currentlyExpandedId;
                this.expandIdRetryCount = 0;
                setTimeout(() => this.expandPanelFromRoute(), 300);
              }
            }
          },
          error: (error) => {
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000
            });
          }
        });
      }
    });
  }

  onAcceptableBloodTypesWarningClick(recipient: Recipient): void {
    // Store currently expanded participant to restore after dismissing the warning
    const currentlyExpandedId = this.openKey$.getValue();

    const dialogRef = this.dialog.open(DismissAcceptableBloodtypesWarningDialogComponent, {
      data: { recipient }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        // Call API to dismiss the warning
        this.participantService.dismissAcceptableBloodTypesWarning(recipient.id!, result.reason).subscribe({
          next: (updatedRecipient) => {
            this.snackBar.open('Acceptable blood types warning dismissed successfully', 'Close', {
              duration: 3000
            });

            // Update the recipient in the participants list
            const recipientIndex = this.participants.findIndex((p) => p.id === recipient.id);
            if (recipientIndex !== -1) {
              // Preserve linkedDonors when updating
              const linkedDonors = (this.participants[recipientIndex] as Recipient).linkedDonors;
              this.participants[recipientIndex] = { ...updatedRecipient, linkedDonors, type: 'Recipient' };

              // Force Angular to detect the change by creating a new array reference
              this.participants = [...this.participants];

              // Reapply filters to update the UI immediately
              this.applyFiltersAndSearch();

              // Restore expanded panel to the pair the user was working on
              if (currentlyExpandedId) {
                this.expandIdFromRoute = currentlyExpandedId;
                this.expandIdRetryCount = 0;
                setTimeout(() => this.expandPanelFromRoute(), 300);
              }
            }
          },
          error: (error) => {
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000
            });
          }
        });
      }
    });
  }

  onMaxDonorAgeWarningClick(recipient: Recipient): void {
    // Store currently expanded participant to restore after dismissing the warning
    const currentlyExpandedId = this.openKey$.getValue();

    const dialogRef = this.dialog.open(DismissMaxDonorAgeWarningDialogComponent, {
      data: { recipient }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        // Call API to dismiss the warning
        this.participantService.dismissMaxDonorAgeWarning(recipient.id!, result.reason).subscribe({
          next: (updatedRecipient) => {
            this.snackBar.open('Max donor age warning dismissed successfully', 'Close', {
              duration: 3000
            });

            // Update the recipient in the participants list
            const recipientIndex = this.participants.findIndex((p) => p.id === recipient.id);
            if (recipientIndex !== -1) {
              // Preserve linkedDonors when updating
              const linkedDonors = (this.participants[recipientIndex] as Recipient).linkedDonors;
              this.participants[recipientIndex] = { ...updatedRecipient, linkedDonors, type: 'Recipient' };

              // Force Angular to detect the change by creating a new array reference
              this.participants = [...this.participants];

              // Reapply filters to update the UI immediately
              this.applyFiltersAndSearch();

              // Restore expanded panel to the pair the user was working on
              if (currentlyExpandedId) {
                this.expandIdFromRoute = currentlyExpandedId;
                this.expandIdRetryCount = 0;
                setTimeout(() => this.expandPanelFromRoute(), 300);
              }
            }
          },
          error: (error) => {
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000
            });
          }
        });
      }
    });
  }

  onMaxMismatchesWarningClick(recipient: Recipient): void {
    // Store currently expanded participant to restore after dismissing the warning
    const currentlyExpandedId = this.openKey$.getValue();

    const dialogRef = this.dialog.open(DismissMaxMismatchesWarningDialogComponent, {
      data: { recipient }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.confirmed) {
        // Call API to dismiss the warning
        this.participantService.dismissMaxMismatchesWarning(recipient.id!, result.reason).subscribe({
          next: (updatedRecipient) => {
            this.snackBar.open('Max mismatches warning dismissed successfully', 'Close', {
              duration: 3000
            });

            // Update the recipient in the participants list
            const recipientIndex = this.participants.findIndex((p) => p.id === recipient.id);
            if (recipientIndex !== -1) {
              // Preserve linkedDonors when updating
              const linkedDonors = (this.participants[recipientIndex] as Recipient).linkedDonors;
              this.participants[recipientIndex] = { ...updatedRecipient, linkedDonors, type: 'Recipient' };

              // Force Angular to detect the change by creating a new array reference
              this.participants = [...this.participants];

              // Reapply filters to update the UI immediately
              this.applyFiltersAndSearch();

              // Restore expanded panel to the pair the user was working on
              if (currentlyExpandedId) {
                this.expandIdFromRoute = currentlyExpandedId;
                this.expandIdRetryCount = 0;
                setTimeout(() => this.expandPanelFromRoute(), 300);
              }
            }
          },
          error: (error) => {
            this.snackBar.open(`Error: ${error.message}`, 'Close', {
              duration: 5000
            });
          }
        });
      }
    });
  }

  // Get current donor index for a recipient (default to 0)
  getCurrentDonorIndex(participantId: string): number {
    return this.currentDonorIndex[participantId] || 0;
  }

  // Navigate to next donor for a recipient
  nextDonor(participantId: string, totalDonors: number): void {
    const currentIndex = this.getCurrentDonorIndex(participantId);
    this.currentDonorIndex[participantId] = (currentIndex + 1) % totalDonors;
  }

  // Navigate to previous donor for a recipient
  previousDonor(participantId: string, totalDonors: number): void {
    const currentIndex = this.getCurrentDonorIndex(participantId);
    this.currentDonorIndex[participantId] = currentIndex === 0 ? totalDonors - 1 : currentIndex - 1;
  }

  // Get the currently displayed donor for a recipient
  getCurrentDonor(participant: Recipient): Donor | undefined {
    if (!participant.linkedDonors || participant.linkedDonors.length === 0) {
      return undefined;
    }
    // Clamped defensively: a stale index must never blank out the donor box.
    const index = Math.min(this.getCurrentDonorIndex(participant.id || ''), participant.linkedDonors.length - 1);
    const linkedDonor = participant.linkedDonors[index];

    // Find the full donor object from participants array to ensure all data is loaded
    if (linkedDonor && linkedDonor.id) {
      const fullDonor = this.participants.find(p => p.id === linkedDonor.id && this.isDonor(p)) as Donor;
      return fullDonor || linkedDonor;
    }

    return linkedDonor;
  }

  // Get withdraw reasons for pairs
  getWithdrawReasons(): Record<string, ReasonInactive> {
    // Exclude automatic reasons that should only be set by the system
    return {
      MedicalProblemsPatient: ReasonInactive.MedicalProblemsPatient,
      MedicalProblemsDonor: ReasonInactive.MedicalProblemsDonor,
      PhychologicalProblemsPatient: ReasonInactive.PhychologicalProblemsPatient,
      PhychologicalProblemsDonor: ReasonInactive.PhychologicalProblemsDonor,
      Other: ReasonInactive.Other
    };
  }

  // Get reasons for donors and altruists (exclude patient-related and automatic reasons)
  getDonorWithdrawReasons(): Record<string, ReasonInactive> {
    return {
      MedicalProblemsDonor: ReasonInactive.MedicalProblemsDonor,
      PhychologicalProblemsDonor: ReasonInactive.PhychologicalProblemsDonor,
      Other: ReasonInactive.Other
    };
  }

  // Get reasons for recipients (exclude donor-related and automatic reasons)
  getRecipientWithdrawReasons(): Record<string, ReasonInactive> {
    return {
      MedicalProblemsPatient: ReasonInactive.MedicalProblemsPatient,
      PhychologicalProblemsPatient: ReasonInactive.PhychologicalProblemsPatient,
      Other: ReasonInactive.Other
    };
  }

  // Helper methods to check if participants can be activated or have their reason changed
  canActivateParticipant(participant: Participant | null | undefined): boolean {
    if (!participant) return false;
    return participant.reasonInactive !== ReasonInactive.AwaitingNewMatchRunPeriod;
  }

  canActivatePairEntity(pair: Pair | null | undefined): boolean {
    if (!pair) return false;
    return pair.reasonInactive !== ReasonInactive.AwaitingNewMatchRunPeriod;
  }

  canChangeInactiveReason(reasonInactive: string | null | undefined): boolean {
    return reasonInactive !== ReasonInactive.AwaitingNewMatchRunPeriod;
  }

  // Pair management methods
  removePairFromParticipant(pair: Pair): void {
    const dialogRef = this.dialog.open(RemoveDialogComponent, {
      data: pair
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.onPairDeleted(pair);
      }
    });
  }

  editPairFromParticipant(pair: Pair): void {
    const dialogRef = this.dialog.open(UnpairDialogComponent, {
      data: { pair: pair },
      width: '500px'
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.action === 'unpair') {
        this.onPairDeleted(pair);
      }
    });
  }

  withdrawPairFromParticipant(pair: Pair, reason: KeyValue<string, ReasonInactive>): void {
    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    pair.reasonInactive = reason.value;
    pair.active = false;
    this.matchingPeriodService.deactivatePair(pair).subscribe({
      next: (updatedPair) => {
        this.onPairUpdated(updatedPair);
        // Note: Don't sync participant status here - backend handles it correctly
        // by only deactivating participants if this is their last active pair
        this.showNotification('Pair set to inactive with reason: ' + reason.value);

        // Store the expanded ID to restore after reload
        if (currentlyExpandedId) {
          this.expandIdFromRoute = currentlyExpandedId;
          this.expandIdRetryCount = 0;
        }

        this.loadParticipants(); // Reload to refresh the UI with correct participant statuses
      },
      error: (err) => {
        console.error('Error editing pair:', err);
        this.showNotification('Failed to set pair inactive. Please try again.');
      }
    });
  }

  changeInactiveReasonFromParticipant(pair: Pair, reason: KeyValue<string, ReasonInactive>): void {
    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    const updatedPair = { ...pair, reasonInactive: reason.value };
    this.pairService.updatePair(updatedPair).subscribe({
      next: (result) => {
        this.onPairUpdated(result);
        this.showNotification('Inactive reason changed to: ' + reason.value);

        // Store the expanded ID to restore after reload
        if (currentlyExpandedId) {
          this.expandIdFromRoute = currentlyExpandedId;
          this.expandIdRetryCount = 0;
        }

        this.loadParticipants(); // Reload to refresh the UI
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  activatePairFromParticipant(pair: Pair): void {
    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    if (pair.reasonInactive && pair.reasonInactive !== 'New matching period') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          pair: pair,
          message: `This pair is inactive with reason: "${pair.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.onPairUpdated(result);
          // Note: Don't sync participant status here - backend handles activation correctly
          this.showNotification('Pair set to active');

          // Store the expanded ID to restore after reload
          if (currentlyExpandedId) {
            this.expandIdFromRoute = currentlyExpandedId;
            this.expandIdRetryCount = 0;
          }

          this.loadParticipants(); // Reload to refresh the UI with correct participant statuses
        }
      });
    } else {
      this.matchingPeriodService.activatePair(pair.id).subscribe({
        next: (updatedPair) => {
          this.onPairUpdated(updatedPair);
          // Note: Don't sync participant status here - backend handles activation correctly
          this.showNotification('Pair set to active');

          // Store the expanded ID to restore after reload
          if (currentlyExpandedId) {
            this.expandIdFromRoute = currentlyExpandedId;
            this.expandIdRetryCount = 0;
          }

          this.loadParticipants(); // Reload to refresh the UI with correct participant statuses
        },
        error: (err) => console.error(err)
      });
    }
  }

  // Altruist management methods
  deactivateAltruistFromParticipant(donor: Donor, reason: KeyValue<string, ReasonInactive>): void {
    if (!donor.id) return;

    const updatedDonor = { ...donor, active: false, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.onDonorUpdated(result);
        this.showNotification('Altruist set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating altruist:', err);
        this.showNotification('Failed to deactivate altruist. Please try again.');
      }
    });
  }

  changeAltruistInactiveReasonFromParticipant(donor: Donor, reason: KeyValue<string, ReasonInactive>): void {
    if (!donor.id) return;

    const updatedDonor = { ...donor, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.onDonorUpdated(result);
        this.refreshPairsForParticipant(donor.id);
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  activateAltruistFromParticipant(donor: Donor): void {
    if (!donor.id) return;

    if (donor.reasonInactive && donor.reasonInactive !== 'New matching period') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          donor: donor,
          message: `This altruist is inactive with reason: "${donor.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.onDonorUpdated(result);
          this.showNotification('Altruist set to active');
        }
      });
    } else {
      const updatedDonor = { ...donor, active: true, reasonInactive: undefined };
      this.participantService.updateDonor(updatedDonor).subscribe({
        next: (result) => {
          this.onDonorUpdated(result);
          this.showNotification('Altruist set to active');
        },
        error: (err) => {
          console.error('Error activating altruist:', err);
          this.showNotification('Failed to activate altruist. Please try again.');
        }
      });
    }
  }

  // Donor management methods
  deactivateDonorFromParticipant(donor: Donor, reason: KeyValue<string, ReasonInactive>): void {
    if (!donor.id) return;

    const updatedDonor = { ...donor, active: false, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.onDonorUpdated(result);
        this.refreshPairsForParticipant(donor.id);
        this.showNotification('Donor set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating donor:', err);
        this.showNotification('Failed to deactivate donor. Please try again.');
      }
    });
  }

  changeDonorInactiveReasonFromParticipant(donor: Donor, reason: KeyValue<string, ReasonInactive>): void {
    if (!donor.id) return;

    const updatedDonor = { ...donor, reasonInactive: reason.value };
    this.participantService.updateDonor(updatedDonor).subscribe({
      next: (result) => {
        this.onDonorUpdated(result);
        this.refreshPairsForParticipant(donor.id);
        this.showNotification('Inactive reason changed to: ' + reason.value);
      },
      error: (err) => {
        console.error('Error changing inactive reason:', err);
        this.showNotification('Failed to change inactive reason. Please try again.');
      }
    });
  }

  activateDonorFromParticipant(donor: Donor): void {
    if (!donor.id) return;

    if (donor.reasonInactive && donor.reasonInactive !== 'New matching period') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          donor: donor,
          message: `This donor is inactive with reason: "${donor.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.onDonorUpdated(result);
          this.refreshPairsForParticipant(donor.id);
          this.showNotification('Donor set to active');
        }
      });
    } else {
      const updatedDonor = { ...donor, active: true, reasonInactive: undefined };
      this.participantService.updateDonor(updatedDonor).subscribe({
        next: (result) => {
          this.onDonorUpdated(result);
          this.refreshPairsForParticipant(donor.id);
          this.showNotification('Donor set to active');
        },
        error: (err) => {
          console.error('Error activating donor:', err);
          this.showNotification('Failed to activate donor. Please try again.');
        }
      });
    }
  }

  // Recipient management methods
  deactivateRecipientFromParticipant(recipient: Recipient, reason: KeyValue<string, ReasonInactive>): void {
    if (!recipient.id) return;

    const updatedRecipient = { ...recipient, active: false, reasonInactive: reason.value };
    this.participantService.updateRecipient(updatedRecipient).subscribe({
      next: (result) => {
        this.onRecipientUpdated(result);
        this.refreshPairsForParticipant(recipient.id);
        this.showNotification('Recipient set to inactive with reason: ' + reason.value);
      },
      error: (err) => {
        console.error('Error deactivating recipient:', err);
        this.showNotification('Failed to deactivate recipient. Please try again.');
      }
    });
  }

  changeRecipientInactiveReasonFromParticipant(recipient: Recipient, reason: KeyValue<string, ReasonInactive>): void {
    if (!recipient.id) return;

    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    // Check if this is a multi-donor group
    const participantPairs = this.getAllPairsForParticipant(recipient.id);
    const isMultiDonorGroup = participantPairs.length > 1;

    if (isMultiDonorGroup) {
      // Change reason for entire group (recipient + all pairs)
      this.participantService.changeRecipientInactiveReasonWithPairs(recipient.id, reason.value).subscribe({
        next: () => {
          this.showNotification('Inactive reason changed for group: ' + reason.value);

          // Store the expanded ID to restore after reload
          if (currentlyExpandedId) {
            this.expandIdFromRoute = currentlyExpandedId;
            this.expandIdRetryCount = 0;
          }

          this.loadParticipants();
        },
        error: (err) => {
          console.error('Error changing inactive reason for group:', err);
          this.showNotification('Failed to change inactive reason for group. Please try again.');
        }
      });
    } else {
      // Single recipient without pairs - just update the recipient
      const updatedRecipient = { ...recipient, reasonInactive: reason.value };
      this.participantService.updateRecipient(updatedRecipient).subscribe({
        next: (result) => {
          this.onRecipientUpdated(result);
          this.showNotification('Inactive reason changed to: ' + reason.value);

          // Store the expanded ID to restore after reload
          if (currentlyExpandedId) {
            this.expandIdFromRoute = currentlyExpandedId;
            this.expandIdRetryCount = 0;
          }

          this.loadParticipants();
        },
        error: (err) => {
          console.error('Error changing inactive reason:', err);
          this.showNotification('Failed to change inactive reason. Please try again.');
        }
      });
    }
  }

  activateRecipientFromParticipant(recipient: Recipient): void {
    if (!recipient.id) return;

    if (recipient.reasonInactive && recipient.reasonInactive !== 'New matching period') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          recipient: recipient,
          message: `This recipient is inactive with reason: "${recipient.reasonInactive}". Are you sure you want to set it to active?`
        },
        width: '500px'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.onRecipientUpdated(result);
          this.refreshPairsForParticipant(recipient.id);
          this.showNotification('Recipient set to active');
        }
      });
    } else {
      const updatedRecipient = { ...recipient, active: true, reasonInactive: undefined };
      this.participantService.updateRecipient(updatedRecipient).subscribe({
        next: (result) => {
          this.onRecipientUpdated(result);
          this.refreshPairsForParticipant(recipient.id);
          this.showNotification('Recipient set to active');
        },
        error: (err) => {
          console.error('Error activating recipient:', err);
          this.showNotification('Failed to activate recipient. Please try again.');
        }
      });
    }
  }

  deactivateRecipientWithPairs(recipient: Recipient, reason: KeyValue<string, ReasonInactive>): void {
    if (!recipient.id) return;

    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    this.participantService.deactivateRecipientWithPairs(recipient.id, reason.value).subscribe({
      next: () => {
        this.showNotification('Group deactivated with reason: ' + reason.value);

        // Store the expanded ID to restore after reload
        if (currentlyExpandedId) {
          this.expandIdFromRoute = currentlyExpandedId;
          this.expandIdRetryCount = 0;
        }

        this.loadParticipants();
      },
      error: (err) => {
        console.error('Error deactivating group:', err);
        this.showNotification('Failed to deactivate group. Please try again.');
      }
    });
  }

  activateRecipientWithPairs(recipient: Recipient): void {
    if (!recipient.id) return;

    // Store currently expanded participant to restore after refresh
    const currentlyExpandedId = this.openKey$.getValue();

    // Use dialog for confirmation if there's an inactive reason
    if (recipient.reasonInactive && recipient.reasonInactive !== 'New matching period') {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        data: {
          message: `This group is inactive with reason: "${recipient.reasonInactive}". Do you want to activate this group and all pairs with the same reason?`
        }
      });

      dialogRef.afterClosed().subscribe((confirmed) => {
        if (confirmed) {
          // Perform the bulk activation
          this.participantService.activateRecipientWithPairs(recipient.id!).subscribe({
            next: () => {
              this.showNotification('Group activated');

              // Store the expanded ID to restore after reload
              if (currentlyExpandedId) {
                this.expandIdFromRoute = currentlyExpandedId;
                this.expandIdRetryCount = 0;
              }

              this.loadParticipants();
            },
            error: (err) => {
              console.error('Error activating group:', err);
              this.showNotification('Failed to activate group. Please try again.');
            }
          });
        }
      });
    } else {
      // No reason or 'New matching period' - activate directly without confirmation
      this.participantService.activateRecipientWithPairs(recipient.id).subscribe({
        next: () => {
          this.showNotification('Group activated');

          // Store the expanded ID to restore after reload
          if (currentlyExpandedId) {
            this.expandIdFromRoute = currentlyExpandedId;
            this.expandIdRetryCount = 0;
          }

          this.loadParticipants();
        },
        error: (err) => {
          console.error('Error activating group:', err);
          this.showNotification('Failed to activate group. Please try again.');
        }
      });
    }
  }

  /**
   * Update the hasMinimatchInactive flag by checking if any participants have
   * reasonInactive === 'Not included in minimatch'
   * Also update hasActiveParticipants to check if there are any active (non-historical) participants
   */
  private updateMinimatchStatus(): void {
    const reason = ReasonInactive.NotIncludedInMinimatch;

    // Check pairs
    const hasPairsWithReason = this.pairs.some(p => !p.active && p.reasonInactive === reason);

    // Check solo participants
    const hasSoloParticipantsWithReason = this.participants.some(participant => {
      if (this.isDonor(participant)) {
        const donor = participant as Donor;
        // Check if it's a solo donor (altruist or standalone)
        const isSolo = donor.altruist || !(participant as any).linkedRecipient;
        return isSolo && !donor.active && donor.reasonInactive === reason;
      } else if (this.isRecipient(participant)) {
        const recipient = participant as Recipient;
        // Check if it's a solo recipient (no linked donors)
        const isSolo = !(participant as any).linkedDonors || (participant as any).linkedDonors.length === 0;
        return isSolo && recipient.active === false && recipient.reasonInactive === reason;
      }
      return false;
    });

    this.hasMinimatchInactive = hasPairsWithReason || hasSoloParticipantsWithReason;

    // Check if there are any active (non-historical) participants
    const hasActivePairs = this.pairs.some(p => p.active);
    const hasActiveSoloParticipants = this.participants.some(participant => {
      if (this.isDonor(participant)) {
        const donor = participant as Donor;
        // Check if it's a solo donor (altruist or standalone) and active
        const isSolo = donor.altruist || !(participant as any).linkedRecipient;
        return isSolo && donor.active && !donor.historical;
      } else if (this.isRecipient(participant)) {
        const recipient = participant as Recipient;
        // Check if it's a solo recipient (no linked donors) and active
        const isSolo = !(participant as any).linkedDonors || (participant as any).linkedDonors.length === 0;
        return isSolo && recipient.active && !recipient.historical;
      }
      return false;
    });

    this.hasActiveParticipants = hasActivePairs || hasActiveSoloParticipants;
  }

  /**
   * Toggle minimatch status for all active participants (ALLOCATION/OPERATOR only)
   * If participants with "Not included in minimatch" exist, revert them to active.
   * Otherwise, set all active participants (excluding locked/selected for transplant) to inactive with this reason.
   */
  toggleMinimatchStatus(): void {
    const dialogRef = this.dialog.open(MinimatchSettingsDialogComponent, {
      width: '600px',
      data: {
        hasMinimatchInactive: this.hasMinimatchInactive
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.success) {
        this.snackBar.open(result.message, 'Close', { duration: 5000 });
        this.loadParticipants();
      }
    });
  }
}

function scrollTo(id?: string) {
  // Get the HTML element
  const el = document.getElementById(`participant-${id}`);
  // If it somehow doesn't exist abort
  if (!el) {
    return;
  }

  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
}
